import { useTranslation } from '@/src/i18n';
import { Ionicons } from '@expo/vector-icons';
import { useState } from 'react';
import {
  TouchableOpacity,
  View,
  Text,
  Platform,
} from 'react-native';
import { createStyles } from './OnboardingScreen.styles';

export interface OnboardingScreenProps {
  onSkip: () => void;
  onCreateTournament: () => void;
  onExplore: () => void;
  onRequestNotificationPermission: () => void;
  colors: {
    background: string;
    text: string;
    textSecondary: string;
    primary: string;
    border: string;
  };
}

interface SlideData {
  icon: keyof typeof Ionicons.glyphMap;
  titleKey: string;
  subtitleKey: string;
  iconBg: string;
  iconColor: string;
}

// Notification primer is only shown on native (mobile) platforms
const SHOW_NOTIFICATION_STEP = Platform.OS !== 'web';

export function OnboardingScreen({
  onSkip,
  onCreateTournament,
  onExplore,
  onRequestNotificationPermission,
  colors,
}: OnboardingScreenProps) {
  const { t } = useTranslation();
  const styles = createStyles();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showNotifStep, setShowNotifStep] = useState(false);

  const slides: SlideData[] = [
    {
      icon: 'trophy',
      titleKey: 'onboarding.slide1Title',
      subtitleKey: 'onboarding.slide1Subtitle',
      iconBg: '#FFF3CD',
      iconColor: '#F59E0B',
    },
    {
      icon: 'people',
      titleKey: 'onboarding.slide2Title',
      subtitleKey: 'onboarding.slide2Subtitle',
      iconBg: '#DBEAFE',
      iconColor: '#3B82F6',
    },
    {
      icon: 'shield-checkmark',
      titleKey: 'onboarding.slide3Title',
      subtitleKey: 'onboarding.slide3Subtitle',
      iconBg: '#EDE9FE',
      iconColor: '#7C3AED',
    },
  ];

  const isLastSlide = currentIndex === slides.length - 1;

  const goToNext = () => {
    if (isLastSlide && SHOW_NOTIFICATION_STEP) {
      setShowNotifStep(true);
    } else {
      setCurrentIndex(currentIndex + 1);
    }
  };

  // Notification primer step
  if (showNotifStep) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.slide}>
          <View style={[styles.iconContainer, { backgroundColor: '#EDE9FE' }]}>
            <Ionicons name="notifications" size={56} color="#8B5CF6" />
          </View>
          <Text style={[styles.slideTitle, { color: colors.text }]}>
            {t('onboarding.notifTitle')}
          </Text>
          <Text style={[styles.slideSubtitle, { color: colors.textSecondary }]}>
            {t('onboarding.notifSubtitle')}
          </Text>
        </View>

        <View style={[styles.footer, { backgroundColor: colors.background }]}>
          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.primary }]}
            onPress={onRequestNotificationPermission}
          >
            <Text style={[styles.primaryButtonText, { color: '#FFFFFF' }]}>
              {t('onboarding.notifEnable')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.secondaryButton, { borderColor: colors.border }]}
            onPress={onCreateTournament}
          >
            <Text style={[styles.secondaryButtonText, { color: colors.textSecondary }]}>
              {t('onboarding.notifLater')}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {!isLastSlide && (
        <TouchableOpacity style={styles.skipButton} onPress={onSkip}>
          <Text style={[styles.skipText, { color: colors.textSecondary }]}>
            {t('onboarding.skip')}
          </Text>
        </TouchableOpacity>
      )}

      <View style={styles.slide}>
        <View style={[styles.iconContainer, { backgroundColor: slides[currentIndex].iconBg }]}>
          <Ionicons name={slides[currentIndex].icon} size={56} color={slides[currentIndex].iconColor} />
        </View>
        <Text style={[styles.slideTitle, { color: colors.text }]}>
          {t(slides[currentIndex].titleKey as any)}
        </Text>
        <Text style={[styles.slideSubtitle, { color: colors.textSecondary }]}>
          {t(slides[currentIndex].subtitleKey as any)}
        </Text>
      </View>

      <View style={[styles.footer, { backgroundColor: colors.background }]}>
        <View style={styles.dotsRow}>
          {slides.map((_, i) => (
            <View
              key={i}
              style={[
                styles.dot,
                {
                  backgroundColor:
                    i === currentIndex ? colors.primary : colors.border,
                  width: i === currentIndex ? 20 : 8,
                },
              ]}
            />
          ))}
        </View>

        {isLastSlide ? (
          <>
            <TouchableOpacity
              style={[styles.primaryButton, { backgroundColor: colors.primary }]}
              onPress={SHOW_NOTIFICATION_STEP ? goToNext : onCreateTournament}
            >
              <Text style={[styles.primaryButtonText, { color: '#FFFFFF' }]}>
                {SHOW_NOTIFICATION_STEP ? t('onboarding.next') : t('onboarding.createTournament')}
              </Text>
            </TouchableOpacity>
            {!SHOW_NOTIFICATION_STEP && (
              <TouchableOpacity
                style={[styles.secondaryButton, { borderColor: colors.primary }]}
                onPress={onExplore}
              >
                <Text style={[styles.secondaryButtonText, { color: colors.primary }]}>
                  {t('onboarding.exploreTournaments')}
                </Text>
              </TouchableOpacity>
            )}
          </>
        ) : (
          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.primary }]}
            onPress={goToNext}
          >
            <Text style={[styles.primaryButtonText, { color: '#FFFFFF' }]}>
              {t('onboarding.next')}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}
